import { type Theme, useTheme } from '@mui/material'
import { type LineCustomSvgLayer, type LineSvgLayer, type PointTooltipComponent, ResponsiveLine } from '@nivo/line'
import {
    decimalToLocalizedString,
    EmptyState,
    formatNumberWithAbbreviation,
    formatNumberWithShortAbbreviation,
    Tab,
    TabBar,
    tokens,
    useColors,
} from 'ComponentLibrary'
import { LineChartTooltip, type LineChartTooltipCopy, type LineChartTooltipTranslations } from './LineChartTooltip'
import { type ComponentProps, useState } from 'react'
import {
    type LineChartSeries,
    type LineChartYearFilteredData,
    type LineChartYearFilterValue,
    type LineData,
    type XAxisPeriod,
} from './types'
import dayjs, { type Dayjs } from 'dayjs'
import { LineChartRangeLayer as rangeArea } from 'ComponentLibrary/src/molecules/Chart/LineChartRange'
import { LineChartCustomLine as customLine } from 'ComponentLibrary/src/molecules/Chart/LineChartCustomLine'
import { LineChartCustomDot as customDot } from 'ComponentLibrary/src/molecules/Chart/LineChartCustomDot'
import { LineChartCustomPrediction as customPrediction } from 'ComponentLibrary/src/molecules/Chart/LineChartCustomPrediction'
import { withNivoTooltipWrapperTheme } from 'ComponentLibrary/src/common/nivoTheme'

export type LineChartProps = {
    data: LineData[]
    period?: XAxisPeriod
    yUnit?: string
    showTrends?: boolean
    shortYFormat?: boolean
    startAtZero?: boolean
    yearOnly?: boolean
    tooltip?: PointTooltipComponent<LineChartSeries>
    customLayer?: LineCustomSvgLayer<LineChartSeries>
    customLegend?: LineCustomSvgLayer<LineChartSeries>
    withYearFilter?: boolean
    yearFilteredData?: LineChartYearFilteredData
    seriesKey?: string
    yearFilterUnitLabel?: string
    bottomSpace?: number
    trimLeadingZeroes?: boolean
    emptyStateTitle?: string
    lineChartTooltipCopy?: Partial<LineChartTooltipCopy>
    calendarWeekLabel?: string
    tooltipTranslations?: LineChartTooltipTranslations
    locale?: string
}

const DEFAULT_EMPTY_STATE_TITLE = 'Es stehen noch nicht genügend Daten zur Verfügung'

export function LineChart({
    data = [],
    yUnit = '',
    period = 'year',
    showTrends = false,
    shortYFormat = true,
    startAtZero = false,
    yearOnly = false,
    bottomSpace = 64,
    tooltip = LineChartTooltip,
    customLayer,
    customLegend,
    withYearFilter,
    yearFilteredData,
    seriesKey,
    yearFilterUnitLabel,
    trimLeadingZeroes = false,
    emptyStateTitle = DEFAULT_EMPTY_STATE_TITLE,
    lineChartTooltipCopy,
    calendarWeekLabel,
    tooltipTranslations,
    locale = 'de-DE',
}: LineChartProps) {
    const theme = useTheme()
    const { getColor } = useColors()
    const resolvedYearFilterUnitLabel = yearFilterUnitLabel ?? getDefaultYearFilterUnitLabel(locale)
    const resolvedCalendarWeekLabel = calendarWeekLabel ?? getDefaultCalendarWeekLabel(locale)

    const dateFormat = getDateFormat(period, resolvedCalendarWeekLabel)
    const tooltipFormat = getTooltipValueFormat(yUnit, locale)
    const tickFormat = getShortNumberFormat(shortYFormat, yUnit, locale)

    const mappedData = mapLineChartData({ data, getColor, showTrends, tooltipFormat, yearOnly, trimLeadingZeroes, locale })

    // The order in the layers array matters. The bottom layers are rendered above other layers.
    const layers: Array<LineSvgLayer<LineChartSeries> | undefined> = [
        'grid',
        'markers',
        'crosshair',
        customLayer,
        customLegend,
        'axes',
        'areas',
        rangeArea,
        customPrediction,
        customLine,
        'slices',
        customDot,
        'legends',
        'mesh',
    ]

    const today = dayjs()
    // get earliest and latest date for series in dayjs format
    const earliestDate = mappedData.reduce((earliest, serie) => {
        const serieEarliest = dayjs(serie.data[0]?.date)
        return serieEarliest.isBefore(earliest) ? serieEarliest : earliest
    }, dayjs())
    const latestDate = mappedData.reduce((latest, serie) => {
        const serieLatest = dayjs(serie.data[serie.data.length - 1]?.date)
        return serieLatest.isAfter(latest) ? serieLatest : latest
    }, dayjs('1970-01-01'))

    const yearsToEarliestDate = today.diff(earliestDate, 'year')
    const yearsToLatestDate = today.diff(latestDate, 'year')
    // If there is more than 10 years of data we want 10 years as the default, however sometimes we only have data older than ten years and then
    // we have an edge case if the default is set to 10. Therefore set to max for this edge case
    const defaultFilter: LineChartYearFilterValue = yearsToEarliestDate > 10 && yearsToLatestDate < 10 ? 10 : -1
    const [selectedFilter, setSelectedFilter] = useState<LineChartYearFilterValue>(defaultFilter)

    const latestDateOrToday = latestDate.isAfter(today) ? latestDate : today

    const yearChips = [3, 5, 10, 15]
        .filter((value) => value <= yearsToEarliestDate)
        .map((value) => ({ title: `${value}${resolvedYearFilterUnitLabel}`, value, flag: selectedFilter === value }))
        .concat({ title: 'MAX', value: -1, flag: selectedFilter === -1 })

    const filteredData = mapLineChartData({
        data: getLineChartDataForSelectedFilter({ data, latestDateOrToday, selectedFilter, yearFilteredData }),
        getColor,
        showTrends,
        tooltipFormat,
        yearOnly,
        trimLeadingZeroes,
        locale,
    })

    const maxFilteredDataLength = filteredData.length > 0 ? Math.max(...filteredData.map((serie) => serie.data.length)) : 0
    const maxDataLength = Math.max(...mappedData.map((serie) => serie.data.length), 0)

    if (maxDataLength === 0) {
        return <EmptyState title={emptyStateTitle} size="enormous" textColor={tokens.ColorTextDefault} />
    }
    const hasTabs = yearChips.length > 2 && withYearFilter
    const yearFilter = hasTabs ? (
        <TabBar type="stretched" selectedTab={selectedFilter} handleChange={(newValue) => setSelectedFilter(newValue)}>
            {yearChips.map((chip) => (
                <Tab key={chip.value} value={chip.value} label={chip.title} />
            ))}
        </TabBar>
    ) : null

    if (maxFilteredDataLength === 0) {
        return (
            <>
                {yearFilter}
                <EmptyState title={emptyStateTitle} size="enormous" textColor={tokens.ColorTextDefault} />
            </>
        )
    }

    const maxYValue = Math.max(...filteredData.map((serie) => Math.max(...serie.data.map((point) => point.range?.max || point.y || 0))))
    const maxYScale = maxFilteredDataLength === 1 ? 2 * maxYValue + 0.5 : maxYValue * 1.05 + 0.5

    if (hasTabs) {
        bottomSpace += 50
    }

    if (customLegend) {
        bottomSpace += 18
        if (customLayer) {
            bottomSpace += 22
        }
    }

    return (
        <>
            {yearFilter}
            <ResponsiveLine<LineChartSeries>
                key={seriesKey}
                data={filteredData}
                useMesh={true}
                curve="linear"
                theme={customTheme(theme)}
                colors={(serie) => serie.color}
                margin={{ top: 24, right: 24, bottom: bottomSpace, left: shortYFormat ? 44 : 76 }}
                xScale={{ format: yearOnly ? '%Y' : '%Y-%m-%d', type: 'time' }}
                xFormat={yearOnly ? 'time:%Y' : `time:${dateFormat}`}
                axisBottom={{
                    format: dateFormat,
                    tickPadding: 9,
                    tickValues: Math.min(maxFilteredDataLength, 10),
                }}
                gridXValues={Math.min(maxFilteredDataLength, 10)}
                yScale={{ type: 'linear', min: startAtZero ? 0 : 'auto', max: maxYScale }}
                yFormat={tooltipFormat}
                axisLeft={{
                    format: tickFormat,
                    tickValues: 5,
                    tickPadding: 10,
                }}
                gridYValues={7}
                tooltip={
                    tooltip === LineChartTooltip
                        ? (point) => (
                              <LineChartTooltip {...point} copy={lineChartTooltipCopy} translations={tooltipTranslations} locale={locale} />
                          )
                        : tooltip
                }
                layers={layers.filter(notEmpty)}
            />
        </>
    )
}

function getLineChartDataForSelectedFilter({
    data,
    latestDateOrToday,
    selectedFilter,
    yearFilteredData,
}: {
    data: LineData[]
    latestDateOrToday: Dayjs
    selectedFilter: LineChartYearFilterValue
    yearFilteredData?: LineChartYearFilteredData
}) {
    const dataForSelectedYearFilter = yearFilteredData?.[selectedFilter]

    if (dataForSelectedYearFilter !== undefined) {
        return dataForSelectedYearFilter
    }

    const cutOffDate = selectedFilter > 0 ? latestDateOrToday.subtract(selectedFilter, 'year') : undefined

    return data.map((serie) => ({
        ...serie,
        data: serie.data.filter((point) => !cutOffDate || dayjs(point.date).isAfter(cutOffDate)),
    }))
}

const getPercentageLabel = (newValue?: number, oldValue?: number, locale = 'de-DE') => {
    if (newValue === undefined || oldValue === undefined || oldValue === 0) {
        return ''
    }
    const percentage = ((newValue - oldValue) / oldValue) * 100
    return `${percentage > 0 ? '+' : ''}${decimalToLocalizedString(percentage, false, 1, undefined, locale)}%`
}

function mapLineChartData({
    data,
    getColor,
    showTrends,
    tooltipFormat,
    yearOnly,
    trimLeadingZeroes,
    locale,
}: {
    data: LineData[]
    getColor: (index: number) => string
    showTrends: boolean
    tooltipFormat: (tick: number | string | Date) => string
    yearOnly: boolean
    trimLeadingZeroes: boolean
    locale: string
}): LineChartSeries[] {
    return data.map(({ color, data: lineData, showTrends: showTrendsOverwrite, ...others }, serieIndex) => {
        const sortedData = lineData.toSorted((a, b) => a.date.localeCompare(b.date))
        // We currently only support predictions into the future, and we only support a single prediction point
        const dataWithoutExtraPredictions = sortedData.filter(
            (point, dataIndex) => !point.isPrediction || dataIndex === sortedData.length - 1
        )
        const visibleData = trimLeadingZeroes ? trimLeadingZeroDataPoints(dataWithoutExtraPredictions) : dataWithoutExtraPredictions

        return {
            ...others,
            color: color || getColor(serieIndex), // add color to each series
            data: visibleData.map(({ date, value, range, ...additionalProps }, dataIndex) => {
                const previousDataPoint = visibleData[dataIndex - 1]
                const tooltipTrendLabel = getPercentageLabel(value, previousDataPoint?.value, locale)

                return {
                    x: date.slice(0, 10), // map date to the x-axis and cut off time
                    y: value as number, // map value to the y-axis
                    range,
                    rangeFormatted: range && {
                        min: {
                            value: tooltipFormat(range.min),
                            trend: getPercentageLabel(range.min, previousDataPoint?.range?.min, locale),
                        },
                        max: {
                            value: tooltipFormat(range.max),
                            trend: getPercentageLabel(range.max, previousDataPoint?.range?.max, locale),
                        },
                    },
                    date,
                    showTrendsOverwrite,
                    label: (showTrendsOverwrite ?? showTrends) ? tooltipTrendLabel : '', // calculate percentage change as additional property
                    tooltipTrendLabel,
                    yearOnly,
                    ...additionalProps,
                }
            }),
        }
    })
}

function trimLeadingZeroDataPoints(dataPoints: LineData['data']) {
    const firstNonZeroIndex = dataPoints.findIndex(({ value }) => value !== 0)
    return firstNonZeroIndex === -1 ? [] : dataPoints.slice(firstNonZeroIndex)
}

function getDefaultYearFilterUnitLabel(locale: string) {
    return locale === 'en-US' ? 'Y' : 'J'
}

function getDefaultCalendarWeekLabel(locale: string) {
    return locale === 'en-US' ? 'CW' : 'KW'
}

const getTooltipValueFormat = (yUnit: string, locale: string) => (tick: number | string | Date) => {
    if (tick instanceof Date) {
        return tick.toLocaleDateString(locale)
    }
    if (typeof tick === 'number') {
        return `${decimalToLocalizedString(tick, false, tick < 100 ? 1 : 0, undefined, locale)}${yUnit}`
    }
    return tick
}

const getShortNumberFormat = (shortFormat: boolean, yUnit: string, locale: string) => (tick: number) =>
    shortFormat
        ? formatNumberWithShortAbbreviation(tick, 2, locale)
        : [formatNumberWithAbbreviation(tick, 2, false, undefined, locale), yUnit].join(' ')

const getDateFormat = (period: XAxisPeriod, calendarWeekLabel: string) => {
    const map = {
        year: '%Y',
        quarter: 'Q%q %Y',
        month: '%b %Y',
        week: `${calendarWeekLabel} %V`,
        day: '%d %b',
    }

    return map[period]
}

function notEmpty<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined
}

const customTheme = (theme: Theme): ComponentProps<typeof ResponsiveLine>['theme'] =>
    withNivoTooltipWrapperTheme({
        axis: {
            ticks: {
                text: {
                    fontSize: theme.typography.bodyTextXSmall.fontSize,
                    fontWeight: theme.typography.bodyTextXSmall.fontWeight,
                    fontFamily: theme.typography.fontFamily,
                    fill: theme.palette.customtext.black54,
                },
                line: {
                    stroke: theme.palette.secondary1.grey,
                },
            },
        },
        grid: {
            line: {
                stroke: theme.palette.secondary1.grey,
            },
        },
    })
